// Iter 55.1b — user-facing copy for known backend error codes.
//
// The backend returns `detail.error` as a stable, machine-readable code
// (e.g. PHONE_ALREADY_IN_USE). This file maps those to a friendly
// title + next_step so the frontend UX stays consistent even if the
// backend `message` field is terse.
//
// A missing entry just means the backend `message` + a status-based
// default are used — this file adds polish for the paths users hit
// most, not exhaustive coverage.

export const KNOWN_ERROR_COPY = {
    // --- Candidate / employer auth ---
    PHONE_ALREADY_IN_USE: {
        title: "That phone is already registered",
        message: "An account with this phone number already exists.",
        next_step: "Log in with that account, or use “Forgot password” if you can't remember it.",
    },
    EMAIL_ALREADY_IN_USE: {
        title: "That email is already registered",
        message: "This email is already in use.",
        next_step: "Log in with your existing account, or use “Forgot password” to reset it.",
    },
    ALREADY_APPLIED_TO_ROLE: {
        title: "You've already applied to this role",
        message: "One of your accounts has already submitted an application for this job.",
        next_step: "Log in with the account you used earlier to see the status. Contact support if you can't remember which account it was.",
    },
    INVALID_OTP: {
        title: "That code didn't match",
        message: "The 6-digit code was incorrect or expired.",
        next_step: "Ask for a new code and try again — codes expire after 10 minutes.",
    },
    OTP_EXPIRED: {
        title: "Code expired",
        message: "This verification code is past its 10-minute window.",
        next_step: "Click “Resend code” to get a fresh one.",
    },
    OTP_COOLDOWN: {
        title: "One moment please",
        message: "You just requested a code very recently.",
        next_step: "Wait a minute before requesting another — the last one is still valid.",
    },
    LOGIN_FAILED: {
        title: "Sign-in failed",
        message: "Email or password didn't match our records.",
        next_step: "Try again, or use “Forgot password” to reset. If you registered with Google, try “Sign in with Google”.",
    },
    ACCOUNT_LOCKED: {
        title: "Account temporarily locked",
        message: "Too many failed sign-in attempts.",
        next_step: "Wait 15 minutes and try again, or reset your password to unlock immediately.",
    },
    UNVERIFIED_EMAIL: {
        title: "Verify your email first",
        message: "This account exists but hasn't been verified yet.",
        next_step: "Check your inbox (and spam) for the 6-digit code we sent when you registered.",
    },

    // --- Apply flow ---
    JOB_NOT_ACCEPTING: {
        title: "This role isn't accepting applications",
        message: "The employer has paused or closed this role.",
        next_step: "Browse other open roles from the jobs list.",
    },
    SCREENING_ALREADY_PASSED: {
        title: "You already passed screening",
        message: "You've already cleared this role's screening.",
        next_step: "Go to your candidate dashboard to book an interview slot.",
    },
    SCREENING_FAILED: {
        title: "Screening not passed",
        message: "Based on your answers, you don't meet the bar for this specific role.",
        next_step: "Explore other roles that better match your background — you can apply to any of them.",
    },

    // --- Uploads ---
    FILE_TOO_LARGE: {
        title: "File is too big",
        message: "That file is larger than we accept.",
        next_step: "Please upload a smaller version (under 10 MB).",
    },
    INVALID_FILE_TYPE: {
        title: "Wrong file type",
        message: "We can't read this file format.",
        next_step: "Upload a PDF, DOC or DOCX file instead.",
    },

    // --- Payments / billing ---
    INSUFFICIENT_BALANCE: {
        title: "Not enough wallet balance",
        message: "This action costs more than you have in your wallet.",
        next_step: "Top up from the Billing page and try again.",
    },
    PAYMENT_FAILED: {
        title: "Payment didn't go through",
        message: "Your bank declined the transaction.",
        next_step: "Try a different card / UPI, or contact your bank. Your wallet was NOT charged.",
    },

    // --- Ops / rate limits ---
    RATE_LIMITED: {
        title: "You're doing that too fast",
        message: "You've hit our per-minute limit.",
        next_step: "Wait 60 seconds and try again.",
    },
};
